import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminNotificationService } from '../../../services/adminNotifications';
import type {
  AdminNotification,
  AdminNotificationFormData,
  NotificationTargeting,
} from '../../../types/adminNotification';

interface CreateNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  notification?: AdminNotification | null;
}

type LanguageTab = 'en' | 'es' | 'de' | 'ru';

export const CreateNotificationModal: React.FC<
  CreateNotificationModalProps
> = ({ isOpen, onClose, notification }) => {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();

  const LANGUAGE_TABS: { key: LanguageTab; label: string }[] = [
    { key: 'en', label: t('notifications.create_modal.languages.english') },
    { key: 'es', label: t('notifications.create_modal.languages.spanish') },
    { key: 'de', label: t('notifications.create_modal.languages.german') },
    { key: 'ru', label: t('notifications.create_modal.languages.russian') },
  ];
  const [activeTab, setActiveTab] = useState<LanguageTab>('en');
  const [recipientCount, setRecipientCount] = useState<number>(0);

  const [formData, setFormData] = useState<AdminNotificationFormData>({
    type: 'announcement',
    title: { en: '', es: '', de: '', ru: '' },
    message: { en: '', es: '', de: '', ru: '' },
    action_url: '',
    targeting: { type: 'all' },
    scheduled_at: undefined,
    expires_after_days: undefined,
  });

  const [targetingType, setTargetingType] = useState<
    'all' | 'role' | 'plan' | 'accounts'
  >('all');
  const [targetingRole, setTargetingRole] = useState<'admin' | 'user'>('user');
  const [targetingPlanId, setTargetingPlanId] = useState<number | undefined>();
  const [targetingAccountIds, setTargetingAccountIds] = useState<number[]>([]);

  // Fetch subscription plans for targeting dropdown
  const { data: subscriptionPlans } = useQuery({
    queryKey: ['subscriptionPlans'],
    queryFn: async () => {
      const response = await fetch('/api/subscription-plans');
      return response.json();
    },
  });

  // Fetch accounts for targeting dropdown
  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      const response = await fetch('/api/admin/accounts');
      return response.json();
    },
  });

  // Preview recipients based on current targeting
  useEffect(() => {
    const fetchRecipientCount = async () => {
      try {
        const targeting = buildTargeting();
        const preview = await adminNotificationService.previewRecipients(
          targeting
        );
        setRecipientCount(preview.count);
      } catch (error) {
        console.error('Failed to fetch recipient count:', error);
      }
    };

    fetchRecipientCount();
  }, [targetingType, targetingRole, targetingPlanId, targetingAccountIds]);

  // Initialize form with notification data if editing
  useEffect(() => {
    if (notification) {
      setFormData({
        type: notification.type,
        title: notification.title,
        message: notification.message,
        action_url: notification.action_url || '',
        targeting: notification.targeting,
        scheduled_at: notification.scheduled_at,
        expires_after_days: notification.expires_after_days,
      });

      // Set targeting state based on notification data
      setTargetingType(notification.targeting.type);
      if (notification.targeting.type === 'role') {
        setTargetingRole(notification.targeting.role);
      } else if (notification.targeting.type === 'plan') {
        setTargetingPlanId(notification.targeting.plan_id);
      } else if (notification.targeting.type === 'accounts') {
        setTargetingAccountIds(notification.targeting.account_ids);
      }
    }
  }, [notification]);

  const buildTargeting = (): NotificationTargeting => {
    switch (targetingType) {
      case 'all':
        return { type: 'all' };
      case 'role':
        return { type: 'role', role: targetingRole };
      case 'plan':
        return { type: 'plan', plan_id: targetingPlanId! };
      case 'accounts':
        return { type: 'accounts', account_ids: targetingAccountIds };
    }
  };

  const createMutation = useMutation({
    mutationFn: adminNotificationService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminNotifications'] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; formData: AdminNotificationFormData }) =>
      adminNotificationService.update(data.id, data.formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminNotifications'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const errors: string[] = [];

    // Check all language fields are filled
    LANGUAGE_TABS.forEach(({ key, label }) => {
      if (!formData.title[key].trim()) {
        errors.push(t('notifications.create_modal.validation.title_required', { language: label }));
      }
      if (!formData.message[key].trim()) {
        errors.push(t('notifications.create_modal.validation.message_required', { language: label }));
      }
    });

    // Check targeting is valid
    if (targetingType === 'role' && !targetingRole) {
      errors.push(t('notifications.create_modal.validation.role_required'));
    }
    if (targetingType === 'plan' && !targetingPlanId) {
      errors.push(t('notifications.create_modal.validation.plan_required'));
    }
    if (targetingType === 'accounts' && targetingAccountIds.length === 0) {
      errors.push(t('notifications.create_modal.validation.accounts_required'));
    }

    if (errors.length > 0) {
      alert('Please fix the following errors:\n\n' + errors.join('\n'));
      return;
    }

    const targeting = buildTargeting();
    const finalData: AdminNotificationFormData = {
      ...formData,
      targeting,
      action_url: formData.action_url || undefined,
      scheduled_at: formData.scheduled_at || undefined,
      expires_after_days: formData.expires_after_days || undefined,
    };

    if (notification) {
      updateMutation.mutate({ id: notification.id, formData: finalData });
    } else {
      createMutation.mutate(finalData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-4xl my-8 mx-4 max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
            <h2 className="text-2xl font-bold">
              {notification ? t('notifications.create_modal.title_edit') : t('notifications.create_modal.title_create')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-6">
            {/* Notification Type */}
            <div>
              <label className="label">{t('notifications.create_modal.form.type_label')}</label>
              <select
                className="input"
                value={formData.type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    type: e.target.value as AdminNotificationFormData['type'],
                  })
                }
              >
                <option value="announcement">{t('notifications.create_modal.form.type_announcement')}</option>
                <option value="maintenance">{t('notifications.create_modal.form.type_maintenance')}</option>
                <option value="security">{t('notifications.create_modal.form.type_security')}</option>
                <option value="marketing">{t('notifications.create_modal.form.type_marketing')}</option>
              </select>
              <p className="text-sm text-gray-500 mt-1">
                {formData.type === 'security' || formData.type === 'maintenance'
                  ? t('notifications.create_modal.form.channel_notice_email')
                  : t('notifications.create_modal.form.channel_notice_inapp')}
              </p>
            </div>

            {/* Language Tabs */}
            <div>
              <div className="flex border-b border-gray-200">
                {LANGUAGE_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-2 font-medium text-sm ${
                      activeTab === tab.key
                        ? 'border-b-2 border-indigo-600 text-indigo-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Language Form Fields */}
              <div className="mt-4 space-y-4">
                <div>
                  <label className="label">
                    {t('notifications.create_modal.form.title_label')} ({LANGUAGE_TABS.find((tab) => tab.key === activeTab)?.label})
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={formData.title[activeTab]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        title: { ...formData.title, [activeTab]: e.target.value },
                      })
                    }
                    placeholder={t(`notifications.create_modal.form.title_placeholder_${activeTab}`)}
                    maxLength={255}
                  />
                </div>

                <div>
                  <label className="label">
                    {t('notifications.create_modal.form.message_label')} ({LANGUAGE_TABS.find((tab) => tab.key === activeTab)?.label})
                  </label>
                  <textarea
                    className="input min-h-[120px]"
                    value={formData.message[activeTab]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        message: {
                          ...formData.message,
                          [activeTab]: e.target.value,
                        },
                      })
                    }
                    placeholder={t(`notifications.create_modal.form.message_placeholder_${activeTab}`)}
                  />
                </div>
              </div>
            </div>

            {/* Action URL */}
            <div>
              <label className="label">{t('notifications.create_modal.form.action_url_label')}</label>
              <input
                type="url"
                className="input"
                value={formData.action_url}
                onChange={(e) =>
                  setFormData({ ...formData, action_url: e.target.value })
                }
                placeholder={t('notifications.create_modal.form.action_url_placeholder')}
              />
              <p className="text-sm text-gray-500 mt-1">
                {t('notifications.create_modal.form.action_url_help')}
              </p>
            </div>

            {/* Targeting */}
            <div>
              <label className="label">{t('notifications.create_modal.form.targeting_label')}</label>
              <div className="space-y-3">
                {/* All Users */}
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="targeting"
                    checked={targetingType === 'all'}
                    onChange={() => setTargetingType('all')}
                    className="mr-2"
                  />
                  <span>{t('notifications.create_modal.form.targeting_all')}</span>
                </label>

                {/* By Role */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="targeting"
                      checked={targetingType === 'role'}
                      onChange={() => setTargetingType('role')}
                      className="mr-2"
                    />
                    <span>{t('notifications.create_modal.form.targeting_role')}</span>
                  </label>
                  {targetingType === 'role' && (
                    <select
                      className="input ml-6 mt-2"
                      value={targetingRole}
                      onChange={(e) =>
                        setTargetingRole(e.target.value as 'admin' | 'user')
                      }
                    >
                      <option value="user">{t('notifications.create_modal.form.role_user')}</option>
                      <option value="admin">{t('notifications.create_modal.form.role_admin')}</option>
                    </select>
                  )}
                </div>

                {/* By Subscription Plan */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="targeting"
                      checked={targetingType === 'plan'}
                      onChange={() => setTargetingType('plan')}
                      className="mr-2"
                    />
                    <span>{t('notifications.create_modal.form.targeting_plan')}</span>
                  </label>
                  {targetingType === 'plan' && (
                    <select
                      className="input ml-6 mt-2"
                      value={targetingPlanId}
                      onChange={(e) =>
                        setTargetingPlanId(Number(e.target.value))
                      }
                    >
                      <option value="">{t('notifications.create_modal.form.plan_placeholder')}</option>
                      {subscriptionPlans?.map((plan: any) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Specific Accounts */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="targeting"
                      checked={targetingType === 'accounts'}
                      onChange={() => setTargetingType('accounts')}
                      className="mr-2"
                    />
                    <span>{t('notifications.create_modal.form.targeting_accounts')}</span>
                  </label>
                  {targetingType === 'accounts' && (
                    <select
                      multiple
                      className="input ml-6 mt-2"
                      value={targetingAccountIds.map(String)}
                      onChange={(e) => {
                        const options = Array.from(e.target.selectedOptions);
                        setTargetingAccountIds(
                          options.map((o) => Number(o.value))
                        );
                      }}
                      size={5}
                    >
                      {accounts?.data?.map((account: any) => (
                        <option key={account.id} value={account.id}>
                          {account.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Recipient Count */}
              <div className="mt-3 p-3 bg-blue-50 rounded">
                <p className="text-sm text-blue-800">
                  <strong>{t('notifications.create_modal.form.recipient_preview_title')}:</strong> {t('notifications.create_modal.form.recipient_preview_users', { count: recipientCount })}
                </p>
              </div>
            </div>

            {/* Schedule */}
            <div>
              <label className="label">{t('notifications.create_modal.form.schedule_label')}</label>
              <input
                type="datetime-local"
                className="input"
                value={formData.scheduled_at || ''}
                onChange={(e) =>
                  setFormData({ ...formData, scheduled_at: e.target.value })
                }
              />
              <p className="text-sm text-gray-500 mt-1">
                {t('notifications.create_modal.form.schedule_help')}
              </p>
            </div>

            {/* Expiry */}
            <div>
              <label className="label">{t('notifications.create_modal.form.expires_label')}</label>
              <input
                type="number"
                className="input"
                min="1"
                max="365"
                value={formData.expires_after_days || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expires_after_days: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }
                placeholder={t('notifications.create_modal.form.expires_placeholder')}
              />
              <p className="text-sm text-gray-500 mt-1">
                {t('notifications.create_modal.form.expires_help')}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              {t('notifications.create_modal.buttons.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? t('notifications.create_modal.buttons.saving')
                : notification
                ? t('notifications.create_modal.buttons.update')
                : t('notifications.create_modal.buttons.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
